import React, {useCallback} from 'react';
import {EIconSmallButton, ETooltip} from 'webapp-lib/Components';

import {Close} from '_/assets/images';
import {typedObserver} from '_/utilities/typed-observer';

import {
  SplayCorner,
  StyledTabBarItem,
  TextContainer,
  TooltipContent,
  TooltipHeader,
} from './styles';

export interface Item {
  text: string;
  id: string;
  width?: number;
  tooltipContent?: string;
  tooltipHeader?: string;
  closeCallback?: () => void;
}

export interface Props<T extends Item>
  extends React.HTMLAttributes<HTMLAnchorElement> {
  active?: boolean;
  className?: string;
  item: T;
  ref?: React.Ref<HTMLAnchorElement>;
  width?: number;
  onActivate: (item: T) => void;
  onClose?: (item: T) => void;
  splayCorner?: boolean;
}

function TabBarItemComponent<T extends Item>({
  className,
  item,
  active,
  onActivate,
  onClose,
  ref,
  splayCorner = false,
  ...props
}: Props<T>): React.ReactElement {
  const handleClick = useCallback(() => {
    onActivate(item);
  }, [onActivate, item]);

  const close = useCallback(() => {
    onClose?.(item);
    item.closeCallback?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, item.closeCallback, item]);

  const handleCloseClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      close();
    },
    [close]
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      /* istanbul ignore else */
      if (event.button === 1) {
        close();
      }
    },
    [close]
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      /* istanbul ignore else */
      if (
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        /* istanbul ignore else */
        if (event.key === 'Enter' || event.key === 'Space') {
          onActivate(item);
        }
      }
    },
    [onActivate, item]
  );

  return (
    <ETooltip
      role='tooltip'
      title={
        item.tooltipHeader || item.tooltipContent ? (
          <>
            <TooltipHeader>{item.tooltipHeader}</TooltipHeader>
            <TooltipContent>{item.tooltipContent}</TooltipContent>
          </>
        ) : (
          ''
        )
      }
    >
      <StyledTabBarItem
        ref={ref}
        active={active}
        className={className}
        role='tab'
        tabIndex={0}
        onClick={handleClick}
        onKeyPress={handleKeyPress}
        onMouseDown={handleMouseDown}
        {...props}
      >
        {splayCorner ? (
          <SplayCorner active={active} data-testid='splay-corner' />
        ) : null}
        <TextContainer>{item.text}</TextContainer>
        {onClose || item.closeCallback ? (
          <EIconSmallButton testId='close' onClick={handleCloseClick}>
            <Close />
          </EIconSmallButton>
        ) : null}
      </StyledTabBarItem>
    </ETooltip>
  );
}

export default typedObserver(TabBarItemComponent);
